import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import atomOneDark from "react-syntax-highlighter/dist/esm/styles/hljs/atom-one-dark";
import { motion } from "motion/react";

export default function ChatMessage({ message, setMessage }) {
  return (
    <div className="space-y-4">
      {message.map((item, index) => (
        <SingleMessage
          key={index}
          message={message}
          setMessage={setMessage}
          index={index}
        />
      ))}
    </div>
  );
}

function SingleMessage({ message, setMessage, index }) {
  const currentMessage = message[index];
  const { role, content, isRendered } = currentMessage;
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    if (role === "user") {
      setDisplayedText(content);
      if (!isRendered) {
        const updatedMessages = [...message];
        updatedMessages[index] = {
          ...updatedMessages[index],
          isRendered: true,
        };
        setMessage(updatedMessages);
      }
      return;
    }

    if (isRendered) {
      setDisplayedText(content);
      return;
    }

    setDisplayedText("");

    let currentIndex = 0;
    const typewriterInterval = setInterval(() => {
      if (currentIndex <= content.length) {
        const newText = content.slice(0, currentIndex);
        setDisplayedText(newText);
        currentIndex++;
      } else {
        clearInterval(typewriterInterval);
        const updatedMessages = [...message];
        updatedMessages[index] = {
          ...updatedMessages[index],
          isRendered: true,
        };
        setMessage(updatedMessages);
      }
    }, 30);

    return () => clearInterval(typewriterInterval);
  }, [content, role, isRendered, index, message, setMessage]);

  return (
    <motion.div
      className={`flex ${
        role === "user" ? "justify-end" : "justify-start"
      } mb-4 p-4`}
      initial={{ opacity: 0.8 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <motion.div
        className={`px-4 py-3 rounded-2xl !text-md md:!text-3xl ${
          role === "user"
            ? "bg-main text-white rounded-br-none"
            : role === "intro"
            ? "text-shade-4 font-light italic"
            : "text-shade-4 font-light"
        }`}
        initial={{ opacity: 0.8 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: role === "user" ? 0 : 0.1,
        }}
        style={{ willChange: "transform" }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({ className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || "");
              return match ? (
                <div className="my-2">
                  <SyntaxHighlighter
                    style={atomOneDark}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      margin: "0",
                    }}
                    {...props}
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                </div>
              ) : (
                <code
                  {...props}
                  className={`px-2 py-0.5 rounded text-xs font-mono ${
                    role === "user"
                      ? "bg-white/20 text-white"
                      : "bg-shade-1 text-shade-0"
                  }`}
                >
                  {children}
                </code>
              );
            },
            p: ({ children }) => (
              <p className="mb-2 last:mb-0 text-sm leading-relaxed">
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc list-inside mb-2 space-y-1 text-sm">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-inside mb-2 space-y-1 text-sm">
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li className="leading-relaxed">{children}</li>
            ),
            h1: ({ children }) => (
              <h1 className="text-lg font-bold mb-2">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-base font-bold mb-2">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-sm font-bold mb-1">{children}</h3>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-main/30 pl-4 italic my-2 text-shade-1">
                {children}
              </blockquote>
            ),
          }}
        >
          {displayedText}
        </ReactMarkdown>
      </motion.div>
    </motion.div>
  );
}
